import * as React from "react";
import { useDialog } from "@shared-ui/dialog-context";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkToolbar } from "uitk-react-toolbar";
import {
  UitkMediaGalleryImageDialog,
  UitkMediaGalleryImageView,
  UitkMediaGalleryThumbnailView,
} from "uitk-react-media-gallery";
import { UitkFullscreenDialog, UitkDialogContent, UitkDialogAnimationType } from "uitk-react-dialog";

import { GalleryCarouselProps } from "../typings";
import { getFullScreenImages } from "components/utility/GalleryImageUtil";
import { UitkGalleryCarousel, UitkGalleryCarouselProps } from "uitk-react-gallery-carousel";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";

const GalleryCarousel = (props: GalleryCarouselProps) => {
  const { title, images, identifier, analyticsModule } = props;
  const track = useClickTracker();
  const { formatText } = useLocalization();
  const galleryImages = getFullScreenImages(images);

  const [currentCarouselIndex, setCurrentCarouselIndex] = React.useState(0);
  const [currentMediaGalleryIndex, setCurrentMediaGalleryIndex] = React.useState(0);

  const mediaGalleryQuery = identifier ? `mediaGallery${identifier}` : "mediaGallery";

  const thumbnailsGalleryQuery = identifier ? `thumbnailsGallery${identifier}` : "thumbnailsGallery";

  const [, mediaDialogActions, MediaGalleryDialogComponent] = useDialog(mediaGalleryQuery);
  const [, thumbnailsDialogActions, ThumbnailsGalleryDialogComponent] = useDialog(thumbnailsGalleryQuery);

  const setCarouselIndex = (index: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarousel",
      rfrr: "carousel.gallery-nav.btn",
      action: Action.CLICK,
      linkName: "Navigating carousel gallery viewer",
    };
    sendImmediateClickstreamTrackEvent(trackingInfo, track);
    setCurrentCarouselIndex(index);
  };

  const openMediaGallery = (index?: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarousel",
      rfrr: "carousel.gallery.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateClickstreamTrackEvent(trackingInfo, track);
    if (index === undefined) {
      setCurrentMediaGalleryIndex(currentCarouselIndex);
    } else {
      setCurrentMediaGalleryIndex(index);
    }

    mediaDialogActions.openDialog();
  };

  const openThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarouselThumbnails",
      rfrr: "carousel.gallery.thumbnails.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateClickstreamTrackEvent(trackingInfo, track);
    thumbnailsDialogActions.openDialog();
  };

  const closeMediaGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarousel",
      rfrr: "carousel.gallery.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateClickstreamTrackEvent(trackingInfo, track);

    mediaDialogActions.closeDialog();
  };

  const closeThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryCarouselThumbnails",
      rfrr: "carousel.gallery.thumbnails.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateClickstreamTrackEvent(trackingInfo, track);
    thumbnailsDialogActions.closeDialog();

    document.getElementById("galleryCarouselOpenButton")?.focus();
  };

  const galleryProps: UitkGalleryCarouselProps = {
    allyHeadingLevel: 2,
    allyHeadingText: title,
    carouselName: analyticsModule,
    carouselId: "destination-image-gallery-carousel",
    images,
    currentIndex: currentCarouselIndex,
    onIndexChange: setCarouselIndex,
    onMediaButtonClick: openThumbnailsGallery,
    onImageClick: openMediaGallery,
    showGalleryButton: true,
  };

  return (
    <div className="GalleryCarousel" data-testid="galleryCarouselImage">
      <UitkGalleryCarousel {...galleryProps} />
      <MediaGalleryDialogComponent>
        <UitkMediaGalleryImageDialog
          animation={UitkDialogAnimationType.NONE}
          dialogShow
          strictFullscreen
          onDismiss={closeMediaGallery}
        >
          <UitkMediaGalleryImageView
            images={galleryImages}
            currentIndex={currentMediaGalleryIndex}
            onIndexChange={setCurrentMediaGalleryIndex}
          />
        </UitkMediaGalleryImageDialog>
      </MediaGalleryDialogComponent>
      <ThumbnailsGalleryDialogComponent>
        <UitkFullscreenDialog
          strictFullscreen={false}
          dialogShow={false}
          animation={UitkDialogAnimationType.SLIDEUP}
          className="uitk-dialog-fullscreen-bg-default"
        >
          <UitkDialogContent className="no-padding">
            <UitkToolbar
              header={title}
              iconLabel={formatText("destinationThumbnailsGallery.button.close")}
              action={closeThumbnailsGallery}
            />
            <UitkMediaGalleryThumbnailView images={images} onImageSelected={openMediaGallery} />
          </UitkDialogContent>
        </UitkFullscreenDialog>
      </ThumbnailsGalleryDialogComponent>
    </div>
  );
};

export default GalleryCarousel;
